import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { layerZeroEndpointByChainId } from 'structure/bridge/constants/layerzeroEndpoints';
import { layerZeroChainIdsByChainId } from 'structure/bridge/constants/lazyerZeroChains';
import { useLZMessage } from 'structure/bridge/hooks/useLZMessage';
import {
  TTransaction,
  transactionsAtom,
} from 'structure/bridge/store/transactionsAtom';
import {
  getTxPayload,
  getTxPayloadHash,
} from 'structure/bridge/utils/oftHelpers';
import { Abi, encodePacked, zeroAddress } from 'viem';
import { useBlockNumber, useReadContract, useReadContracts } from 'wagmi';
import LZ_ENDPOINT_ABI from '../../constants/abis/endpoint_abi';
import { BridgeTransaction } from './BridgeTransaction';
import { MessageStatus } from '@layerzerolabs/scan-client';
import { TFailedMessage } from 'structure/bridge/constants/types';
import { useQueryClient } from '@tanstack/react-query';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';

export type TFailedMessagesInfo = {
  status: string;
  result: `0x${string}`;
};

interface Props {
  transaction: TTransaction;
}

export function BridgeTransactionInFlight({ transaction }: Props) {
  const [transactions, setTransactions] = useAtom(transactionsAtom);
  const { message } = useLZMessage(transaction); // sets the status of the transaction
  const [latestNonce, setLatestNonce] = useState<bigint>();
  const calculatedPayload = getTxPayload(transaction);
  const calculatedPayloadHash = getTxPayloadHash(calculatedPayload);

  const remoteAndLocalPath = encodePacked(
    ['address', 'address'],
    [
      transaction.token.oftAddress[transaction.srcChainId] ?? zeroAddress, // remote relative to the endpoint
      transaction.token.oftAddress[transaction.dstChainId] ?? zeroAddress, // local relative to the endpoint
    ]
  );

  // get nonces from the endpoint while waiting for the message to be received
  // const localOftInboundNonceBefore = await localEndpoint.getInboundNonce(remoteChainId, remoteAndLocalPath);
  const inboundNonceQuery = useReadContract({
    query: {
      enabled: transaction.status === MessageStatus.INFLIGHT,
    },
    address: (layerZeroEndpointByChainId as Record<number, string>)[
      transaction.dstChainId
    ] as `0x${string}`,
    chainId: transaction.dstChainId,
    abi: LZ_ENDPOINT_ABI,
    functionName: 'getInboundNonce',
    args: [
      layerZeroChainIdsByChainId[transaction.srcChainId], // remote chain id relative to the endpoint
      remoteAndLocalPath,
    ],
  });

  useEffect(() => {
    if (inboundNonceQuery.data) {
      // set initial nonce in transaction
      if (transaction.initialNonce === '0') {
        setTransactions((prev) => {
          const index = prev.findIndex(
            (tx) => tx.srcTxHash === transaction.srcTxHash
          );
          prev[index].initialNonce = inboundNonceQuery.data.toString();
          return prev;
        });
      }
      // set latest nonce
      if (latestNonce !== inboundNonceQuery.data) {
        setLatestNonce(inboundNonceQuery.data);
      }
    }
  }, [inboundNonceQuery.data]);

  // LOOK UP FAILED MESSAGES
  const contracts = useMemo(() => {
    const newContracts = [];
    for (
      let i = BigInt(transaction.initialNonce);
      i <= (latestNonce ?? BigInt(0));
      i++
    ) {
      newContracts.push({
        address:
          transaction.token.oftAddress[transaction.dstChainId] ?? zeroAddress,
        chainId: transaction.dstChainId,
        abi: [
          {
            inputs: [
              {
                internalType: 'uint16',
                name: '',
                type: 'uint16',
              },
              {
                internalType: 'bytes',
                name: '',
                type: 'bytes',
              },
              {
                internalType: 'uint64',
                name: '',
                type: 'uint64',
              },
            ],
            name: 'failedMessages',
            outputs: [
              {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
              },
            ],
            stateMutability: 'view',
            type: 'function',
          },
        ] as Abi,
        functionName: 'failedMessages',
        args: [
          layerZeroChainIdsByChainId[transaction.srcChainId], // remote chain id relative to the endpoint
          remoteAndLocalPath, // remote and local path
          i, // nonce on endpoint
        ],
      });
    }
    return newContracts;
    // we only update on changes to nonce
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestNonce]);

  // call the contract to check if the message failed
  const failedMessagesQuery = useReadContracts({
    contracts: contracts,
  });

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: failedMessagesQuery.queryKey });
  }, [blockNumber, queryClient]);

  useEffect(() => {
    if (!failedMessagesQuery.data) {
      return;
    }

    const data = failedMessagesQuery.data;

    for (let i = 0; i < failedMessagesQuery.data.length; i++) {
      // check if a message is failed and update the transactions
      if (
        // @ts-expect-error strict mode must be enabled (abi type inference)
        data[i]?.result?.toLowerCase() === calculatedPayloadHash.toLowerCase()
      ) {
        const failedMessage: TFailedMessage = {
          _srcChainId:
            layerZeroChainIdsByChainId[transaction.srcChainId].toString(),
          _srcAddress: remoteAndLocalPath,
          _nonce: (parseInt(transaction.initialNonce) + i).toString(),
          _payload: calculatedPayload,
          transactionHash: transaction.srcTxHash,
        };
        const previousTransactions = transactions;
        const indexOfTransaction = previousTransactions.findIndex(
          (tx) => tx.srcTxHash === transaction.srcTxHash
        );

        previousTransactions[indexOfTransaction].failedMessage = failedMessage;
        setTransactions([...previousTransactions]);
      }
    }
  }, [failedMessagesQuery.data]);

  // set tx lz status
  useEffect(() => {
    const updatedTransactions = transactions.map((tx) => {
      if (tx.srcTxHash === transaction.srcTxHash) {
        return {
          ...tx,
          status: message.status,
          dstTxHash: message.dstTxHash as `0x${string}`,
        };
      }
      return tx;
    });
    setTransactions(updatedTransactions);
    // we only want to check the message status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  return <BridgeTransaction transaction={transaction} />;
}
